import { useCallback, useEffect, useState } from 'react';
import { icons } from '../../components/ui/Icons';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { getErrorMessage, formatRelativeTime } from '../../lib/adminApi';

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down';
  checks: Array<{
    name: string;
    status: 'pass' | 'warn' | 'fail';
    message?: string;
    timestamp: string;
  }>;
  version: string;
  uptime: number;
  memory: {
    used: number;
    total: number;
    percentage: number;
  };
}

async function fetchHealthApi(): Promise<SystemHealth> {
  const response = await fetch('/health', {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export function SystemStatusPage() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchHealthApi();
      setHealth(data);
    } catch (error) {
      setError(getErrorMessage(error, 'Systemstatus konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'System', href: '/admin/system' },
    { label: 'Systemstatus', current: true },
  ];

  const statusColors = {
    healthy: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    degraded: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    down: 'bg-emergency-100 text-emergency-700 dark:bg-emergency-900/30 dark:text-emergency-300',
  };

  const checkStatusColors = {
    pass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    warn: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    fail: 'bg-emergency-100 text-emergency-700 dark:bg-emergency-900/30 dark:text-emergency-300',
  };

  const checkStatusLabels = {
    pass: 'OK',
    warn: 'Warnung',
    fail: 'Fehler',
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const parts = [];
    if (days) parts.push(`${days}T`);
    if (hours) parts.push(`${hours}Std`);
    if (minutes) parts.push(`${minutes}Min`);
    return parts.join(' ') || '< 1 Min';
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading && !health) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={breadcrumbItems} />
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Systemstatus</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Gesundheitsprüfungen und Systeminformationen.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-neutral-200 dark:border-neutral-700">
              <CardContent className="py-6">
                <div className="animate-pulse h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={breadcrumbItems} />
        <Alert variant="error" title="Fehler beim Laden des Systemstatus" dismissible onDismiss={() => setError(null)}>
          {error}
          <Button variant="outline" size="sm" className="mt-2" leftIcon="RefreshCw" onClick={fetchHealth}>
            Erneut versuchen
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Systemstatus</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Gesundheitsprüfungen und Systeminformationen.</p>
        </div>
        <Button variant="outline" size="sm" leftIcon="RefreshCw" onClick={fetchHealth}>
          Aktualisieren
        </Button>
      </div>

      {/* Overall Status */}
      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Gesamtstatus</p>
              <div className="flex items-center gap-3 mt-1">
                <Badge variant="primary" className={cn('text-lg px-4 py-2', statusColors[health?.status || 'down'])}>
                  {health?.status === 'healthy' ? 'Gesund' : health?.status === 'degraded' ? 'Degradiert' : 'Ausfall'}
                </Badge>
                <span className="text-sm text-neutral-500 dark:text-neutral-400">
                  Letzte Prüfung: {health ? formatRelativeTime(health.checks[0]?.timestamp || '') : '-'}
                </span>
              </div>
            </div>
            <div className="flex gap-4 text-sm">
              <div>
                <p className="text-neutral-500 dark:text-neutral-400">Version</p>
                <p className="font-mono font-medium">{health?.version || '-'}</p>
              </div>
              <div>
                <p className="text-neutral-500 dark:text-neutral-400">Laufzeit</p>
                <p className="font-mono font-medium">{health ? formatUptime(health.uptime) : '-'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Memory Usage */}
      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <icons.HardDrive className="h-5 w-5" aria-hidden="true" />
            Speicherverbrauch
          </CardTitle>
        </CardHeader>
        <CardContent>
          {health && (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-neutral-500 dark:text-neutral-400">Verwendet</span>
                  <span className="font-medium">{formatBytes(health.memory.used)} / {formatBytes(health.memory.total)}</span>
                </div>
                <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-300',
                      health.memory.percentage > 80 ? 'bg-emergency-500' : health.memory.percentage > 60 ? 'bg-amber-500' : 'bg-emerald-500'
                    )}
                    style={{ width: `${Math.min(health.memory.percentage, 100)}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {health.memory.percentage.toFixed(1)}% ausgelastet
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Health Checks */}
      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <icons.Activity className="h-5 w-5" aria-hidden="true" />
              Gesundheitsprüfungen
            </CardTitle>
            <CardDescription>Status der einzelnen Systemkomponenten.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" leftIcon="RefreshCw" onClick={fetchHealth}>
            Prüfen
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {health && health.checks.length > 0 ? (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {health.checks.map((check) => (
                <div
                  key={check.name}
                  className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant="primary" className={cn('text-sm', checkStatusColors[check.status])}>
                      {checkStatusLabels[check.status]}
                    </Badge>
                    <div>
                      <p className="font-medium text-neutral-900 dark:text-white truncate">{check.name}</p>
                      {check.message && <p className="text-sm text-neutral-500 dark:text-neutral-400">{check.message}</p>}
                    </div>
                  </div>
                  <span className="text-xs text-neutral-400 font-mono whitespace-nowrap">
                    {formatRelativeTime(check.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <icons.Activity className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400">Keine Gesundheitsprüfungen verfügbar.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardHeader>
          <CardTitle>Schnellzugriff</CardTitle>
          <CardDescription>Häufig genutzte Systemaktionen.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" leftIcon="Activity" onClick={fetchHealth}>
              Status prüfen
            </Button>
            <Button variant="outline" leftIcon="History" asChild>
              <a href="/admin/security/audit">Audit-Log prüfen</a>
            </Button>
            <Button variant="outline" leftIcon="Settings" asChild>
              <a href="/admin/organization/settings">Einstellungen</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}